/**
 * Tiny module-singleton event bus used to let the chat agent broadcast
 * Solr query URLs to other components on the host page (e.g. the mock
 * search results panel). The host page subscribes; the chat publishes.
 *
 * Why a module singleton instead of window.dispatchEvent?
 *  - Cleaner than polluting the global object.
 *  - Trivially mockable in tests by importing the same module.
 *  - Other React subtrees on the same page can import this module
 *    and subscribe — it works just like a Context but without the
 *    Provider boilerplate.
 */

const bus = new EventTarget();

export const SOLR_QUERY_EVENT = 'solr-query-url';
export const OPEN_CHAT_EVENT = 'voyager-enhanced-search-open';
export const CHAT_BUTTON_CONFIG_EVENT = 'voyager-enhanced-search-button-config';

const DEFAULT_CHAT_WITH_PDF_BUTTON_TEXT = 'Chat with PDF';
let activeChatButtonConfig: {
  owner: symbol;
  chatWithPdfButtonText: string;
} | null = null;

export interface SolrQueryEventDetail {
  url: string;
  ts: number;
}

export interface OpenChatEventDetail {
  documentTitle?: string;
  documentUrl?: string;
  source?: string;
  uuid?: string;
  ts: number;
}

export interface ChatButtonConfig {
  chatWithPdfButtonText: string;
}

/**
 * Publish a Solr query URL. Called from QueryLink whenever the agent
 * generates a new query URL via tool_data.
 */
export function publishSolrQueryUrl(url: string): void {
  bus.dispatchEvent(
    new CustomEvent<SolrQueryEventDetail>(SOLR_QUERY_EVENT, {
      detail: { url, ts: Date.now() },
    }),
  );
}

/**
 * Subscribe to Solr query URL events. Returns an unsubscribe function.
 *
 * @example
 * useEffect(() => subscribeSolrQueryUrl((url) => {
 *   fetch(url).then(...)
 * }), [])
 */
export function subscribeSolrQueryUrl(
  handler: (url: string, ts: number) => void,
): () => void {
  const listener = (e: Event) => {
    const detail = (e as CustomEvent<SolrQueryEventDetail>).detail;
    handler(detail.url, detail.ts);
  };
  bus.addEventListener(SOLR_QUERY_EVENT, listener);
  return () => bus.removeEventListener(SOLR_QUERY_EVENT, listener);
}

export function publishOpenChatModal(
  detail: Omit<OpenChatEventDetail, 'ts'> = {},
): void {
  bus.dispatchEvent(
    new CustomEvent<OpenChatEventDetail>(OPEN_CHAT_EVENT, {
      detail: { ...detail, ts: Date.now() },
    }),
  );
}

export function subscribeOpenChatModal(
  handler: (detail: OpenChatEventDetail) => void,
): () => void {
  const listener = (e: Event) => {
    handler((e as CustomEvent<OpenChatEventDetail>).detail);
  };
  bus.addEventListener(OPEN_CHAT_EVENT, listener);
  return () => bus.removeEventListener(OPEN_CHAT_EVENT, listener);
}

function publishChatButtonConfig(): void {
  bus.dispatchEvent(
    new CustomEvent<ChatButtonConfig | null>(CHAT_BUTTON_CONFIG_EVENT, {
      detail: activeChatButtonConfig
        ? {
            chatWithPdfButtonText: activeChatButtonConfig.chatWithPdfButtonText,
          }
        : null,
    }),
  );
}

export function registerChatButtonConfig(
  config: Partial<ChatButtonConfig> = {},
): () => void {
  const owner = Symbol('voyager-enhanced-search-block');
  activeChatButtonConfig = {
    owner,
    chatWithPdfButtonText:
      config.chatWithPdfButtonText?.trim() || DEFAULT_CHAT_WITH_PDF_BUTTON_TEXT,
  };
  publishChatButtonConfig();

  return () => {
    if (activeChatButtonConfig?.owner === owner) {
      activeChatButtonConfig = null;
      publishChatButtonConfig();
    }
  };
}

export function subscribeChatButtonConfig(
  handler: (config: ChatButtonConfig | null) => void,
): () => void {
  const listener = (e: Event) => {
    handler((e as CustomEvent<ChatButtonConfig | null>).detail);
  };

  bus.addEventListener(CHAT_BUTTON_CONFIG_EVENT, listener);
  handler(
    activeChatButtonConfig
      ? {
          chatWithPdfButtonText: activeChatButtonConfig.chatWithPdfButtonText,
        }
      : null,
  );

  return () => bus.removeEventListener(CHAT_BUTTON_CONFIG_EVENT, listener);
}
